import { Avatar, Button, EmptyState, ErrorState, formatDate, formatTime, Icon, Skeleton, useToast } from "@helmr/ui";
import { useQueryClient } from "@tanstack/react-query";
import { Fragment, useEffect, useMemo, useRef, useState, type PointerEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { isApiError, newIdempotencyKey } from "../../api/client";
import { patchConversation, sendText, type Assignee, type Conversation, type ThreadMessage } from "../../api/inbox";
import { handOverSentinel } from "../../app/mobile/useSheetBack";
import { useServices } from "../../app/services";
import { today as localDate } from "../../lib/dates";
import { isUnmasked, telHref } from "../../lib/phone";
import { FailureBody } from "../home/states";
import { AssignSheet, InfoSheet, TemplateFlow } from "./Sheets";
import { dayLabel, firstName } from "./format";
import { useAssignees, useConversation, useCounts, useInboxAccess, useInboxParams, useNow, useThread } from "./hooks";
import { StatusTick } from "./StatusTick";
import { windowState, HOUR } from "./window";
import { inbox as t } from "./strings";
import s from "./inbox.module.css";

const EDGE = 24;
const BACK_DISTANCE = 80;

/** The server sends the key only: `renewal_reminder` reads as "Renewal reminder". */
const templateLabel = (key: string | null | undefined) => { const w = (key ?? "").replace(/_/g, " "); return w.charAt(0).toUpperCase() + w.slice(1); };

function Bubble({ m: raw, timeZone, staff }: { m: ThreadMessage; timeZone: string; staff: readonly Assignee[] }) {
  if (raw.kind === "system") return <div className={s.sysn} role="note">{raw.body}</div>;
  const m = { ...raw, sentBy: raw.sentBy ?? staff.find((a) => a.id === raw.sentByStaffId) ?? null };
  const out = m.direction === "out";
  const time = formatTime(m.at, timeZone);
  const meta = <span className={s.mt}>{m.kind === "template" && m.sentBy ? <span className={s.sentBy}>{t.thread.sentBy(m.sentBy.name)}</span> : null}{time}{out && m.status ? <StatusTick status={m.status} /> : null}</span>;
  if (m.kind === "template") {
    return (
      <div className={`${s.msg} ${s.out} ${s.tpl}`} data-kind="template">
        <span className={s.tph}><Icon name="file" size={14} />{m.templateName ?? templateLabel(m.templateKey)}</span>
        <span className={s.tpb}>{m.body}</span>
        {meta}
        {(m.buttons ?? []).map((b) => <span key={b} className={s.cta2} aria-disabled="true" title={t.thread.buttonInert}>{b}</span>)}
      </div>
    );
  }
  return (
    <div className={`${s.msg} ${out ? s.out : s.in}`} data-kind={m.kind}>
      {out && m.sentBy ? <span className={s.by}>{m.sentBy.name}</span> : null}
      <span>{m.kind === "media" ? (m.body ?? t.thread.mediaNote) : m.body}</span>
      {meta}
    </div>
  );
}

export function Thread({ id, phone }: { id: string; phone: boolean }) {
  const { client } = useServices();
  const { ctx, canReply, canManage } = useInboxAccess();
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const { search } = useInboxParams();
  const now = useNow();
  const conv = useConversation(id);
  const thread = useThread(id);
  const counts = useCounts(null);
  const staff = useAssignees(!!thread.data).data ?? [];
  const [infoOpen, setInfoOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [tplOpen, setTplOpen] = useState(false);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [outbox, setOutbox] = useState<ThreadMessage[]>([]);
  const [forcedFor, setForcedFor] = useState<number | null | undefined>(undefined);
  const [sentFor, setSentFor] = useState<number | null | undefined>(undefined);
  const intent = useRef<{ sig: string; key: string } | null>(null);
  const body = useRef<HTMLDivElement>(null);
  const edge = useRef<{ x: number; y: number } | null>(null);
  const [slide, setSlide] = useState(0);
  const marked = useRef<string | null>(null);
  const back = `/inbox${search}`;

  const c: Conversation | undefined = conv.data;
  // Opening a thread marks it read, once per open.
  useEffect(() => {
    if (!c || !canManage || c.unreadCount === 0 || marked.current === c.id) return;
    marked.current = c.id;
    patchConversation(client, c.id, { read: true }).then(() => qc.invalidateQueries({ queryKey: ["inbox"] })).catch(() => { marked.current = null; });
  }, [c, canManage, client, qc]);

  // Pages arrive newest first, each oldest-first inside.
  const messages = useMemo(() => [...[...(thread.data?.pages ?? [])].reverse().flatMap((p) => p.messages), ...outbox], [thread.data, outbox]);
  // Follows the newest message only: loading earlier ones must not jump to the bottom.
  const newest = messages.at(-1)?.id;
  useEffect(() => { const el = body.current; if (el) el.scrollTop = el.scrollHeight; }, [newest]);

  const win = windowState(c?.windowOpenUntil, now);
  const closed = !win.open || (c !== undefined && forcedFor === c.windowOpenUntil);
  const name = c ? (c.name ?? t.newContact) : "";
  const first = firstName(c?.name) || "them";
  const lastWrote = c ? (c.lastInboundAt ?? (c.windowOpenUntil !== null ? c.windowOpenUntil - 24 * HOUR : null)) : null;
  const today = localDate(now, ctx.timeZone);

  const keyFor = (sig: string) => {
    if (intent.current?.sig !== sig) intent.current = { sig, key: newIdempotencyKey() };
    return intent.current.key;
  };

  async function submitText() {
    const msg = text.trim();
    if (!msg || sending || !c) return;
    const key = keyFor(`text:${id}:${msg}`);
    const local: ThreadMessage = { id: `local:${key}`, direction: "out", kind: "text", body: msg, status: "pending", at: now, sentBy: null };
    setSending(true);
    setText("");
    setOutbox((o) => [...o.filter((x) => x.id !== local.id), local]);
    try {
      await sendText(client, id, { body: msg }, key);
      intent.current = null;
      await qc.invalidateQueries({ queryKey: ["inbox"] });
    } catch (err) {
      // The text was never lost: it goes back in the box, and a closed window flips the composer without losing it.
      setText(msg);
      if (isApiError(err) && err.code === "window_closed") { setForcedFor(c.windowOpenUntil); toast.show(t.composer.closedFlip); intent.current = null; }
      else toast.show(t.composer.sendFailed);
    } finally {
      setOutbox((o) => o.filter((x) => x.id !== local.id));
      setSending(false);
    }
  }

  const edgeDown = (e: PointerEvent<HTMLDivElement>) => { if (phone && e.clientX <= EDGE) edge.current = { x: e.clientX, y: e.clientY }; };
  const edgeMove = (e: PointerEvent<HTMLDivElement>) => {
    const g = edge.current;
    if (!g) return;
    const dx = e.clientX - g.x;
    if (Math.abs(e.clientY - g.y) > Math.abs(dx) * 1.5 && dx < 12) { edge.current = null; setSlide(0); return; }
    setSlide(Math.max(0, dx));
  };
  const edgeUp = (e: PointerEvent<HTMLDivElement>) => {
    const g = edge.current;
    edge.current = null;
    setSlide(0);
    if (g && e.clientX - g.x >= BACK_DISTANCE) navigate(back);
  };

  const header = (
    <header className={s.thh}>
      {phone ? <Link to={back} className={s.bk} aria-label={t.thread.back}><Icon name="back" size={22} /></Link> : null}
      {c ? (
        <>
          <button type="button" className={s.who2} onClick={() => setInfoOpen(true)} aria-haspopup="dialog" aria-label={t.thread.info(name)}>
            <Avatar id={c.id} name={name} />
            <span><b>{name}</b><small>{t.thread.typeLabel[c.kind]}{c.record ? ` · ${c.record.label}` : ""}</small></span>
          </button>
          <button type="button" className={s.asb} onClick={() => setAssignOpen(true)} aria-haspopup="dialog" aria-label={t.thread.assign(c.assignee?.name ?? t.assignee.none)} disabled={!canManage}>
            {c.assignee ? <Avatar id={c.assignee.id} name={c.assignee.name} size="xs" /> : null}{c.assignee ? (c.assignee.id === ctx.me?.staff.id ? t.assignee.you : firstName(c.assignee.name)) : t.assignee.none}<Icon name="down" size={14} />
          </button>
        </>
      ) : <div className={s.who2}><Skeleton width={40} height={40} style={{ borderRadius: "50%" }} /><Skeleton width={140} height={14} /></div>}
    </header>
  );

  const status = conv.isPending || thread.isPending ? "loading" : conv.error || thread.error ? "error" : "ready";
  const err = conv.error ?? thread.error;
  // A missing route and a missing chat are both `404 not_found`: only the counts tell "not deployed" from "no such chat".
  const notConnected = isApiError(err) && err.status === 404 && isApiError(counts.error) && counts.error.status === 404;

  return (
    <div className={s.thread} data-phone={phone || undefined} style={slide ? { transform: `translateX(${slide}px)` } : undefined}
      onPointerDown={edgeDown} onPointerMove={edgeMove} onPointerUp={edgeUp} onPointerCancel={edgeUp} data-testid="thread">
      {header}
      <div className={s.thb} ref={body} aria-live="polite" aria-busy={status === "loading" || undefined}>
        {status === "loading" ? (
          <div className={s.thSk} aria-label={t.thread.loading}>{[60, 40, 70, 50].map((w, i) => <Skeleton key={i} width={`${w}%`} height={42} style={{ alignSelf: i % 2 ? "flex-end" : "flex-start", borderRadius: 16 }} />)}</div>
        ) : status === "error" ? (
          notConnected ? <FailureBody error={err} title={t.thread.error} what={t.notConnected} onRetry={() => { void conv.refetch(); void thread.refetch(); }} />
            : isApiError(err) && err.status === 404 ? <EmptyState icon="chat" title={t.thread.notFound} action={<Button as={Link} to={back}>{t.thread.back}</Button>} />
            : <ErrorState title={t.thread.error} message={isApiError(err) ? err.message : ""} requestId={isApiError(err) ? err.requestId : null} onRetry={() => { void conv.refetch(); void thread.refetch(); }} />
        ) : messages.length === 0 ? (
          <EmptyState compact icon="chat" title={t.thread.empty} description={t.thread.emptyBody} />
        ) : <>{thread.hasNextPage ? <button type="button" className={s.more} onClick={() => void thread.fetchNextPage()} disabled={thread.isFetchingNextPage}>{thread.isFetchingNextPage ? "Loading…" : t.thread.loadEarlier}</button> : null}{messages.map((m, i) => {
          const day = localDate(m.at, ctx.timeZone);
          const prev = messages[i - 1];
          return (
            <Fragment key={m.id}>
              {!prev || localDate(prev.at, ctx.timeZone) !== day ? <div className={s.dsep}>{dayLabel(day, today, ctx.locale)}</div> : null}
              <Bubble m={m} timeZone={ctx.timeZone} staff={staff} />
            </Fragment>
          );
        })}</>}
      </div>
      {c && canReply ? (
        <Composer
          name={first} closed={closed} windowLabel={win.open ? win.label : null} sentBanner={closed && sentFor === c.windowOpenUntil}
          lastWrote={lastWrote !== null ? formatDate(localDate(lastWrote, ctx.timeZone), ctx.locale, "weekday") : null}
          text={text} onText={setText} onSend={() => void submitText()} sending={sending} onTemplate={() => setTplOpen(true)}
        />
      ) : null}
      {c ? (
        <>
          <InfoSheet open={infoOpen} onOpenChange={setInfoOpen} c={c} lastWrote={lastWrote} canManage={canManage} phoneUnmasked={isUnmasked(c.phone)} telHref={isUnmasked(c.phone) ? telHref(c.phone) : null} onArchived={() => { if (phone) { handOverSentinel(); navigate(back, { replace: true }); } }} />
          <AssignSheet open={assignOpen} onOpenChange={setAssignOpen} c={c} />
          <TemplateFlow open={tplOpen} onOpenChange={setTplOpen} c={c} intentKey={keyFor} onSent={() => { intent.current = null; setSentFor(c.windowOpenUntil); }} />
        </>
      ) : null}
    </div>
  );
}

function Composer({ name, closed, windowLabel, sentBanner, lastWrote, text, onText, onSend, sending, onTemplate }: {
  name: string; closed: boolean; windowLabel: string | null; sentBanner: boolean; lastWrote: string | null;
  text: string; onText: (v: string) => void; onSend: () => void; sending: boolean; onTemplate: () => void;
}) {
  const q = t.composer;
  return (
    <div className={s.cmp}>
      {closed ? (
        sentBanner
          ? <div className={`${s.wbn} ${s.wbnOk}`} role="status"><Icon name="check" size={16} />{q.sentBanner(name)}</div>
          : <div className={s.wbn} role="status"><Icon name="lock" size={16} />{q.closedBanner(name, lastWrote)}</div>
      ) : null}
      <div className={s.cmpt} role="group" aria-label={q.quickLabel}>
        {closed ? <button type="button" disabled><Icon name="clock" size={15} />{q.quickDisabled}</button> : q.quick.map((r) => <button key={r.label} type="button" onClick={() => onText(r.text.replace("{f}", name))}>{r.label}</button>)}
        {closed ? <button type="button" className={s.pri} onClick={onTemplate}><Icon name="file" size={15} />{q.sendTemplate}</button> : <button type="button" onClick={onTemplate}><Icon name="file" size={15} />{q.template}</button>}
        {windowLabel ? <span className={s.wn} data-open="true"><Icon name="clock" size={13} />{windowLabel}</span> : null}
      </div>
      <form className={s.cmpr} onSubmit={(e) => { e.preventDefault(); onSend(); }}>
        <input value={text} onChange={(e) => onText(e.target.value)} disabled={closed} placeholder={closed ? q.closedPlaceholder : q.placeholder} aria-label={q.placeholder} autoComplete="off" enterKeyHint="send" />
        <button type="submit" className={s.snd} aria-label={q.send} disabled={closed || sending || text.trim() === ""}><Icon name="send" size={20} /></button>
      </form>
    </div>
  );
}
